import React, { useState, useEffect } from 'react';
import {
  Users, Building2, Calendar, ChevronDown,
  IndianRupee, Briefcase, Activity, AlertTriangle,
  RefreshCw, UserPlus, UserMinus, Clock,
  BarChart3, PieChart
} from 'lucide-react';
import { employeeAPI, establishmentAPI, wagesAPI, payrollAPI, reportsAPI, invoiceAPI } from '../api/apiService';
import { useAuth, useActiveEstId } from './AuthContext';
import './EstablishmentCard.css';
import './PayrollInsights.css';

const PayrollInsights = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  const [selectedEstablishment, setSelectedEstablishment] = useState(lockedEstId || 'all');
  const [selectedYear,  setSelectedYear]  = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  const [establishments, setEstablishments] = useState([]);
  const [employees,      setEmployees]      = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [misData,        setMisData]        = useState(null);
  const [misLoading,     setMisLoading]     = useState(false);
  const [totalRevenue,   setTotalRevenue]   = useState(0);
  const [insights,       setInsights]       = useState({
    workforce: {}, payroll: {}, attendance: {}, trends: {}, alerts: []
  });

  const monthNames = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
  ];
  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1, currentYear - 2];

  useEffect(() => {
    if (lockedEstId) setSelectedEstablishment(lockedEstId);
    fetchInitialData();
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (establishments.length > 0) {
      generateInsights();
      fetchMISData();
      fetchInvoiceRevenue();
    }
  }, [selectedEstablishment, selectedYear, selectedMonth, establishments, employees]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMISData = async () => {
    try {
      setMisLoading(true);
      const params = { month: selectedMonth, year: selectedYear };
      if (selectedEstablishment !== 'all') params.establishment_id = selectedEstablishment;
      const response = await reportsAPI.getMIS(params);
      setMisData(response.mis || response);
    } catch {
      setMisData(null);
    } finally {
      setMisLoading(false);
    }
  };

  const fetchInvoiceRevenue = async () => {
    try {
      const params = { month: selectedMonth, year: selectedYear };
      if (selectedEstablishment !== 'all') params.establishment_id = selectedEstablishment;
      const response = await invoiceAPI.getList(params);
      const revenue = (response.invoices || []).reduce((sum, inv) => sum + (parseFloat(inv.grand_total) || 0), 0);
      setTotalRevenue(revenue);
    } catch {
      setTotalRevenue(0);
    }
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [estRes, empRes] = await Promise.all([
        establishmentAPI.getAll(),
        employeeAPI.getAll(),
      ]);
      setEstablishments(estRes.establishments || []);
      setEmployees(empRes.employees || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const generateInsights = async () => {
    try {
      const filtered = selectedEstablishment === 'all'
        ? employees
        : employees.filter(emp => emp.establishment_id === parseInt(selectedEstablishment));

      setInsights({
        workforce:  calculateWorkforceInsights(filtered),
        payroll:    calculatePayrollInsights(filtered),
        attendance: calculateAttendanceInsights(filtered),
        trends:     calculateTrendInsights(filtered),
        alerts:     [],
      });
    } catch { /* silent */ }
  };

  const calculateWorkforceInsights = (emps) => {
    const byDepartment = {}, byGender = { male: 0, female: 0, other: 0 };
    const byStatus = { active: 0, inactive: 0 };
    const byType   = { permanent: 0, contract: 0, temporary: 0 };

    emps.forEach(emp => {
      const dept = emp.department || 'Unassigned';
      byDepartment[dept] = (byDepartment[dept] || 0) + 1;

      const g = (emp.gender || '').toLowerCase().trim();
      if (g === 'male' || g === 'm') byGender.male++;
      else if (g === 'female' || g === 'f') byGender.female++;
      else byGender.other++;

      byStatus[emp.is_active ? 'active' : 'inactive']++;

      const t = (emp.employment_type || 'permanent').toLowerCase().trim();
      if (t === 'contract') byType.contract++;
      else if (t === 'temporary' || t === 'part-time' || t === 'parttime') byType.temporary++;
      else byType.permanent++;
    });

    return {
      total: emps.length,
      byDepartment, byGender, byStatus, byType,
      topDepartment: Object.keys(byDepartment).sort((a, b) => byDepartment[b] - byDepartment[a])[0] || 'N/A',
    };
  };

  const calculatePayrollInsights = (emps) => {
    let totalBasic = 0, totalGross = 0, totalDeductions = 0, totalNet = 0;
    const salaryRanges = { '<10k': 0, '10k-25k': 0, '25k-50k': 0, '50k+': 0 };

    emps.forEach(emp => {
      const basic  = parseFloat(emp.basic_salary || 0);
      const gross  = basic + parseFloat(emp.hra || 0) + parseFloat(emp.conveyance_allowance || 0)
                           + parseFloat(emp.medical_allowance || 0) + parseFloat(emp.special_allowance || 0);
      const deductions = parseFloat(emp.pf_contribution || 0) + parseFloat(emp.esi_contribution || 0)
                       + parseFloat(emp.professional_tax || 0);
      totalBasic += basic; totalGross += gross; totalDeductions += deductions; totalNet += gross - deductions;

      if (gross < 10000) salaryRanges['<10k']++;
      else if (gross < 25000) salaryRanges['10k-25k']++;
      else if (gross < 50000) salaryRanges['25k-50k']++;
      else salaryRanges['50k+']++;
    });

    return {
      totalBasic, totalGross, totalDeductions, totalNet,
      avgSalary: emps.length > 0 ? totalGross / emps.length : 0,
      salaryRanges, monthlyBudget: totalGross,
      deductionRate: totalGross > 0 ? (totalDeductions / totalGross * 100) : 0,
    };
  };

  const calculateAttendanceInsights = (emps) => ({
    totalWorkdays: 26, avgPresent: 23.5, avgAbsent: 2.5,
    attendanceRate: (23.5 / 26 * 100), overtimeHours: 45,
    avgOvertimePerEmployee: emps.length > 0 ? 45 / emps.length : 0,
  });

  const calculateTrendInsights = (emps) => {
    const prev = Math.floor(emps.length * 0.97);
    return {
      headcountGrowth: prev > 0 ? ((emps.length - prev) / prev * 100) : 0,
      turnoverRate: 2.3, newJoinees: Math.floor(emps.length * 0.05),
      separations: Math.floor(emps.length * 0.02), retentionRate: 97.7,
    };
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000)   return `₹${(amount / 100000).toFixed(2)} L`;
    if (amount >= 1000)     return `₹${(amount / 1000).toFixed(1)} K`;
    return `₹${amount.toFixed(0)}`;
  };

  // ── Single-color bar chart (enterprise neutral) ────────────────────────────
  const ChartBar = ({ label, value, maxValue }) => {
    const pct = maxValue > 0 ? (value / maxValue * 100) : 0;
    return (
      <div className="pi-bar-row">
        <div className="pi-bar-meta">
          <span className="pi-bar-label">{label}</span>
          <span className="pi-bar-count">{value}</span>
        </div>
        <div className="pi-bar-track">
          <div className="pi-bar-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="pi-loading">
        <div className="pi-spinner" />
        <p className="pi-loading-text">Loading insights…</p>
      </div>
    );
  }

  const selectedEst = selectedEstablishment !== 'all'
    ? establishments.find(e => String(e.id) === String(selectedEstablishment))
    : null;

  const kpiCards = [
    {
      label:   'Total Employees',
      value:   misLoading ? '—' : (misData?.totalEmployees ?? 0),
      sub:     'Active employees',
      variant: 'pi-kpi-blue',
      color:   '#2563eb',
      icon:    Users,
    },
    {
      label:   'New Joinees',
      value:   misLoading ? '—' : (misData?.newJoinees ?? 0),
      sub:     `Joined in ${monthNames[selectedMonth - 1]}`,
      variant: 'pi-kpi-green',
      color:   '#15803d',
      icon:    UserPlus,
    },
    {
      label:   'Employees Relieved',
      value:   misLoading ? '—' : (misData?.relieved ?? 0),
      sub:     `Relieved in ${monthNames[selectedMonth - 1]}`,
      variant: 'pi-kpi-red',
      color:   '#b91c1c',
      icon:    UserMinus,
    },
    {
      label:   'Working Hours',
      value:   misLoading ? '—' : (misData?.totalWorkingHours ?? 0),
      sub:     'Total hours worked',
      variant: 'pi-kpi-indigo',
      color:   '#4338ca',
      icon:    Clock,
    },
    {
      label:   'Employee Turnover',
      value:   misLoading ? '—' : `${(misData?.employeeTurnoverRate ?? 0).toFixed(1)}%`,
      sub:     'Attrition rate',
      variant: 'pi-kpi-amber',
      color:   '#b45309',
      icon:    Activity,
    },
    {
      label:   'Revenue Turnover',
      value:   misLoading ? '—' : formatCurrency(totalRevenue),
      sub:     'Total invoiced revenue',
      variant: 'pi-kpi-purple',
      color:   '#6d28d9',
      icon:    IndianRupee,
    },
  ];

  const deptMax  = Math.max(...Object.values(insights.workforce.byDepartment || { _: 0 }));
  const salMax   = Math.max(...Object.values(insights.payroll.salaryRanges   || { _: 0 }));
  const typeMax  = Math.max(...Object.values(insights.workforce.byType        || { _: 0 }));
  const genMax   = Math.max(...Object.values(insights.workforce.byGender      || { _: 0 }));

  return (
    <div className="pi-page">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="pi-header">
        <div className="pi-header-left">
          <div className="pi-header-icon">
            <BarChart3 size={20} />
          </div>
          <div>
            <h1 className="pi-header-title">Monthly Information Service</h1>
            <p className="pi-header-subtitle">
              {selectedEst ? <><strong>{selectedEst.name}</strong> · </> : null}
              Workforce &amp; payroll intelligence for {monthNames[selectedMonth - 1]} {selectedYear}
            </p>
          </div>
        </div>
        <button className="pi-btn-refresh" onClick={generateInsights}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* ── Filters ────────────────────────────────────────────────────── */}
      <div className="pi-filters">
        {!lockedEstId && (
          <div className="pi-filter-field">
            <label className="pi-filter-label">Organization</label>
            <select
              value={selectedEstablishment}
              onChange={(e) => setSelectedEstablishment(e.target.value)}
              className="pi-select"
            >
              <option value="all">All Organizations</option>
              {establishments.map(est => (
                <option key={est.id} value={est.id}>{est.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="pi-filter-field">
          <label className="pi-filter-label">Month</label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            className="pi-select"
          >
            {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div className="pi-filter-field">
          <label className="pi-filter-label">Year</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="pi-select"
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* ── KPI Cards ──────────────────────────────────────────────────── */}
      <div className="pi-kpi-grid">
        {kpiCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.label} className={`pi-kpi-card ${card.variant}`}>
              <div className="pi-kpi-top">
                <span className="pi-kpi-label">{card.label}</span>
                <Icon size={16} className="pi-kpi-icon" style={{ color: card.color }} />
              </div>
              <div className="pi-kpi-value">{card.value}</div>
              <div className="pi-kpi-sub">{card.sub}</div>
            </div>
          );
        })}
      </div>

      {/* ── Workforce Distribution ──────────────────────────────────────── */}
      <div className="pi-section-header">
        <PieChart size={15} />
        Workforce Distribution
      </div>

      <div className="pi-dist-grid">

        {/* Department */}
        <div className="pi-dist-card">
          <div className="pi-dist-card-title">
            <Building2 size={14} /> Department Distribution
          </div>
          {Object.keys(insights.workforce.byDepartment || {}).length > 0 ? (
            Object.entries(insights.workforce.byDepartment)
              .sort((a, b) => b[1] - a[1])
              .map(([dept, count]) => (
                <ChartBar key={dept} label={dept} value={count} maxValue={deptMax} />
              ))
          ) : (
            <p className="pi-empty-chart">No department data available</p>
          )}
        </div>

        {/* Salary Range */}
        <div className="pi-dist-card">
          <div className="pi-dist-card-title">
            <IndianRupee size={14} /> Salary Range Distribution
          </div>
          <ChartBar label="< ₹10,000"          value={insights.payroll.salaryRanges?.['<10k']    || 0} maxValue={salMax} />
          <ChartBar label="₹10,000 – ₹25,000"  value={insights.payroll.salaryRanges?.['10k-25k'] || 0} maxValue={salMax} />
          <ChartBar label="₹25,000 – ₹50,000"  value={insights.payroll.salaryRanges?.['25k-50k'] || 0} maxValue={salMax} />
          <ChartBar label="> ₹50,000"           value={insights.payroll.salaryRanges?.['50k+']    || 0} maxValue={salMax} />
        </div>

        {/* Employment Type */}
        <div className="pi-dist-card">
          <div className="pi-dist-card-title">
            <Briefcase size={14} /> Employment Type
          </div>
          <ChartBar label="Permanent"  value={insights.workforce.byType?.permanent  || 0} maxValue={typeMax} />
          <ChartBar label="Contract"   value={insights.workforce.byType?.contract   || 0} maxValue={typeMax} />
          <ChartBar label="Temporary"  value={insights.workforce.byType?.temporary  || 0} maxValue={typeMax} />
        </div>

        {/* Gender */}
        <div className="pi-dist-card">
          <div className="pi-dist-card-title">
            <Users size={14} /> Gender Distribution
          </div>
          <ChartBar label="Male"   value={insights.workforce.byGender?.male   || 0} maxValue={genMax} />
          <ChartBar label="Female" value={insights.workforce.byGender?.female || 0} maxValue={genMax} />
          <ChartBar label="Other"  value={insights.workforce.byGender?.other  || 0} maxValue={genMax} />
        </div>

      </div>
    </div>
  );
};

export default PayrollInsights;
